/**
 * Act3ResultScreen — 第三幕最终结局展示
 */
import React from 'react';
import { Heart, Sparkles, Snowflake, AlertTriangle, XCircle, Battery, Star, ArrowRight } from 'lucide-react';
import { useRootStore } from '../../stores/rootStore';
import type { Act3Outcome } from '@shared/types/gameState';

const OUTCOME_CONFIG: Record<Act3Outcome, { icon: React.ReactNode; color: string; label: string }> = {
  '修成正果': { icon: <Heart size={32} className="text-red-500" />, color: 'from-red-50 to-pink-50 border-red-200', label: '恭喜！两人修成正果' },
  '携手成长': { icon: <Sparkles size={32} className="text-amber-500" />, color: 'from-amber-50 to-yellow-50 border-amber-200', label: '虽有波折，但携手前行' },
  '理性止损': { icon: <Snowflake size={32} className="text-blue-500" />, color: 'from-blue-50 to-cyan-50 border-blue-200', label: '理性选择，好聚好散' },
  '承诺恐惧': { icon: <AlertTriangle size={32} className="text-purple-500" />, color: 'from-purple-50 to-indigo-50 border-purple-200', label: '承诺恐惧，止步不前' },
  '崩盘点': { icon: <XCircle size={32} className="text-red-600" />, color: 'from-red-50 to-orange-50 border-red-300', label: '关系崩盘，不欢而散' },
  '消耗殆尽': { icon: <Battery size={32} className="text-gray-500" />, color: 'from-gray-50 to-slate-50 border-gray-200', label: '精力耗尽，无力维系' },
};

const SCORE_COLORS: Record<string, string> = {
  S: 'text-amber-500 bg-amber-50 border-amber-200',
  A: 'text-green-600 bg-green-50 border-green-200',
  B: 'text-blue-600 bg-blue-50 border-blue-200',
  C: 'text-gray-600 bg-gray-50 border-gray-200',
  F: 'text-red-600 bg-red-50 border-red-200',
};

export const Act3ResultScreen: React.FC = () => {
  const root = useRootStore();
  const result = root.act3Result;

  if (!result) return null;

  const config = OUTCOME_CONFIG[result.outcome];
  const scoreColor = SCORE_COLORS[result.score] || SCORE_COLORS.C;

  const handleContinue = () => {
    // TODO: Phase 10 — show final report
    root.resetAll();
  };

  return (
    <div className="h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-cream to-[#F5EDE3]">
      <div className={`max-w-md w-full bg-gradient-to-br ${config.color} border rounded-2xl p-6 shadow-lg`}>
        {/* Score badge */}
        <div className="flex justify-end mb-2">
          <span className={`text-2xl font-black px-3 py-1 rounded-lg border ${scoreColor}`}>
            {result.score}
          </span>
        </div>

        {/* Outcome */}
        <div className="flex flex-col items-center mb-6">
          {config.icon}
          <h2 className="text-xl font-bold mt-3 text-[#2D2D2D]">{result.title}</h2>
          <p className="text-sm text-[#6B7280] mt-1">{config.label}</p>
        </div>

        {/* Narrative */}
        <div className="bg-white/50 rounded-xl p-4 mb-4">
          <p className="text-sm text-[#6B5B4F] leading-relaxed">{result.narrative}</p>
        </div>

        {/* Final metrics */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <MetricBox label={`${root.protagonistA?.name || '男方'} 承诺`} value={result.finalCommitmentA} />
          <MetricBox label={`${root.protagonistB?.name || '女方'} 承诺`} value={result.finalCommitmentB} />
          <MetricBox label="稳定性" value={result.finalStability} />
        </div>

        {/* Highlights */}
        {result.highlights.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xs font-medium text-[#9CA3AF] uppercase tracking-wider mb-2">关键时刻</h3>
            <div className="space-y-1.5">
              {result.highlights.map((h, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Star size={10} className="text-amber-400 mt-1 shrink-0" />
                  <span className="text-xs text-[#6B5B4F]">{h}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Continue button */}
        <button
          onClick={handleContinue}
          className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-coral-500 to-amber-accent hover:from-coral-700 hover:to-coral-500 shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2"
        >
          返回主菜单 <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

const MetricBox: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="bg-white/50 rounded-xl p-2 text-center">
    <span className="text-[9px] text-[#9CA3AF] block">{label}</span>
    <span className="text-lg font-bold text-[#2D2D2D]">{value}</span>
  </div>
);

export default Act3ResultScreen;
